import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  initializeConsent,
  acceptAll,
  acceptNecessary,
  rejectAll,
  setCustomConsent,
  getStoredConsent,
  COOKIE_SETTINGS_EVENT,
} from '../utils/consent';
import './CookieConsent.css';

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analyticsChecked, setAnalyticsChecked] = useState(true);
  const [advertisingChecked, setAdvertisingChecked] = useState(false);

  useEffect(() => {
    // Check if user has already given consent
    const { hasConsent } = initializeConsent();
    if (!hasConsent) {
      // Show banner after a short delay for better UX
      setTimeout(() => setShowBanner(true), 500);
    }
  }, []);

  // Let visitors change their choice later (footer "Cookie settings", Cookie Policy page)
  useEffect(() => {
    const open = () => {
      const stored = getStoredConsent();
      if (stored) {
        setAnalyticsChecked(stored.analytics !== false);
        setAdvertisingChecked(!!stored.advertising);
      }
      setShowDetails(true);
      setShowBanner(true);
    };
    window.addEventListener(COOKIE_SETTINGS_EVENT, open);
    return () => window.removeEventListener(COOKIE_SETTINGS_EVENT, open);
  }, []);

  const handleAcceptAll = () => {
    acceptAll();
    setShowBanner(false);
  };

  const handleAcceptNecessary = () => {
    acceptNecessary();
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    rejectAll();
    setShowBanner(false);
  };

  const handleCustomSave = () => {
    setCustomConsent({
      analytics: analyticsChecked,
      advertising: advertisingChecked,
    });
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div className="cookie-consent-overlay" onClick={(e) => e.stopPropagation()}>
      <div className="cookie-consent-banner">
        <div className="cookie-consent-content">
          <h3 className="cookie-consent-title">Cookie Preferences</h3>
          
          {!showDetails ? (
            <div className="cookie-consent-main">
              <div className="cookie-consent-text-wrapper">
                <p className="cookie-consent-text">
                  We use cookies to enhance your browsing experience, analyze site traffic,
                  and personalize content. By clicking &quot;Accept All&quot;, you consent to our use of cookies.
                  See our{' '}
                  <Link to="/cookie-policy" className="cookie-link">
                    Cookie Policy
                  </Link>
                  {' '}and{' '}
                  <Link to="/privacy-policy" className="cookie-link">
                    Privacy Policy
                  </Link>
                  .{' '}
                  <button
                    type="button"
                    className="cookie-link"
                    onClick={handleAcceptNecessary}
                  >
                    Necessary Only
                  </button>
                  {' or '}
                  <button
                    type="button"
                    className="cookie-link"
                    onClick={handleRejectAll}
                  >
                    Reject All
                  </button>
                </p>
              </div>
              
              <div className="cookie-consent-actions">
                <button
                  className="cookie-btn cookie-btn-primary"
                  onClick={handleAcceptAll}
                >
                  Accept All
                </button>
                <button
                  className="cookie-btn cookie-btn-secondary"
                  onClick={() => setShowDetails(true)}
                >
                  Customize
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="cookie-consent-main">
                <div className="cookie-consent-text-wrapper">
                  <p className="cookie-consent-text">
                    Choose which cookies you want to accept. You can change these preferences at any time.
                  </p>
                </div>
                
                <div className="cookie-consent-actions">
                  <button
                    className="cookie-btn cookie-btn-primary"
                    onClick={handleCustomSave}
                  >
                    Save Preferences
                  </button>
                  <button
                    className="cookie-btn cookie-btn-text"
                    onClick={() => setShowDetails(false)}
                  >
                    Back
                  </button>
                </div>
              </div>
              
              <div className="cookie-consent-options">
                <div className="cookie-option">
                  <label className="cookie-option-label">
                    <input
                      type="checkbox"
                      checked={analyticsChecked}
                      onChange={(e) => setAnalyticsChecked(e.target.checked)}
                      disabled
                    />
                    <div className="cookie-option-content">
                      <strong>Analytics Cookies</strong>
                      <span className="cookie-option-description">
                        Required for website analytics and performance measurement. 
                        These cookies help us understand how visitors interact with our site.
                      </span>
                    </div>
                  </label>
                </div>
                
                <div className="cookie-option">
                  <label className="cookie-option-label">
                    <input
                      type="checkbox"
                      checked={advertisingChecked}
                      onChange={(e) => setAdvertisingChecked(e.target.checked)}
                    />
                    <div className="cookie-option-content">
                      <strong>Advertising Cookies</strong>
                      <span className="cookie-option-description">
                        Used for personalized advertising and remarketing. 
                        These cookies help us show you relevant ads based on your interests.
                      </span>
                    </div>
                  </label>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
